import JSZip from 'jszip';
import { DEFAULT_APPEARANCE } from './constants';
import { getDescendantFolderIds } from './workspace';

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');

  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function sanitizeFileName(value = 'Untitled') {
  return String(value)
    .trim()
    .split('')
    .map((character) =>
      character.charCodeAt(0) < 32 || '<>:"/\\|?*'.includes(character) ? '-' : character
    )
    .join('')
    .replace(/\s+/g, ' ')
    .replace(/^\.+$/, 'Untitled')
    .slice(0, 120) || 'Untitled';
}

function getBaseFileName(file) {
  return sanitizeFileName(file.name || 'Untitled');
}

export function downloadTextFile(file) {
  const blob = new Blob([file.content || ''], { type: 'text/plain;charset=utf-8' });
  downloadBlob(blob, `${getBaseFileName(file)}.txt`);
}

function escapeHtml(value = '') {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function escapeAttribute(value = '') {
  return escapeHtml(value).replace(/`/g, '&#096;');
}

function safeCssValue(value, fallback) {
  const text = String(value || '');

  if (!text || /[<>{};]/.test(text)) {
    return fallback;
  }

  return text;
}

function renderTextNode(node) {
  let html = escapeHtml(node.text || '').replace(/\n/g, '<br />');
  const format = Number(node.format || 0);

  if (format & 16) {
    html = `<code>${html}</code>`;
  }

  if (format & 1) {
    html = `<strong>${html}</strong>`;
  }

  if (format & 2) {
    html = `<em>${html}</em>`;
  }

  if (format & 8) {
    html = `<u>${html}</u>`;
  }

  if (format & 4) {
    html = `<s>${html}</s>`;
  }

  return html;
}

function renderChildren(children = []) {
  return children.map(renderNode).join('');
}

function renderElement(tagName, children, attributes = '') {
  const content = renderChildren(children) || '<br />';
  return `<${tagName}${attributes}>${content}</${tagName}>`;
}

function renderNode(node) {
  if (!node || typeof node !== 'object') {
    return '';
  }

  if (node.type === 'text') {
    return renderTextNode(node);
  }

  if (node.type === 'linebreak') {
    return '<br />';
  }

  if (node.type === 'paragraph') {
    return renderElement('p', node.children);
  }

  if (node.type === 'heading') {
    const tag = /^h[1-6]$/.test(node.tag) ? node.tag : 'h1';
    return renderElement(tag, node.children);
  }

  if (node.type === 'quote') {
    return renderElement('blockquote', node.children);
  }

  if (node.type === 'list') {
    const tag = node.tag === 'ol' || node.listType === 'number' ? 'ol' : 'ul';
    return renderElement(tag, node.children);
  }

  if (node.type === 'listitem') {
    return renderElement('li', node.children);
  }

  if (node.type === 'link' || node.type === 'autolink') {
    const href = typeof node.url === 'string' ? node.url : '#';
    return renderElement(
      'a',
      node.children,
      ` href="${escapeAttribute(href)}" target="_blank" rel="noreferrer"`
    );
  }

  return renderChildren(node.children);
}

function applyMarkdownTextFormat(text, format = 0) {
  let value = text;

  if (format & 16) {
    value = `\`${value.replace(/`/g, '\\`')}\``;
  }

  if (format & 1) {
    value = `**${value}**`;
  }

  if (format & 2) {
    value = `_${value}_`;
  }

  if (format & 8) {
    value = `<u>${value}</u>`;
  }

  if (format & 4) {
    value = `~~${value}~~`;
  }

  return value;
}

function renderMarkdownChildren(children = [], context = {}) {
  return children.map((child, index) => renderMarkdownNode(child, { ...context, index })).join('');
}

function renderMarkdownListItem(node, context) {
  const marker =
    context.listType === 'number'
      ? `${Number(node.value || context.index + 1)}. `
      : '- ';
  const content = renderMarkdownChildren(node.children, context).trim() || ' ';

  return `${marker}${content}\n`;
}

function renderMarkdownNode(node, context = {}) {
  if (!node || typeof node !== 'object') {
    return '';
  }

  if (node.type === 'text') {
    return applyMarkdownTextFormat(node.text || '', Number(node.format || 0));
  }

  if (node.type === 'linebreak') {
    return '\n';
  }

  if (node.type === 'paragraph') {
    return `${renderMarkdownChildren(node.children, context).trim()}\n\n`;
  }

  if (node.type === 'heading') {
    const level = Number((node.tag || 'h1').replace('h', '')) || 1;
    return `${'#'.repeat(Math.min(6, Math.max(1, level)))} ${renderMarkdownChildren(node.children, context).trim()}\n\n`;
  }

  if (node.type === 'quote') {
    const content = renderMarkdownChildren(node.children, context)
      .trim()
      .split('\n')
      .map((line) => `> ${line}`)
      .join('\n');

    return `${content}\n\n`;
  }

  if (node.type === 'list') {
    return `${renderMarkdownChildren(node.children, {
      ...context,
      listType: node.listType || (node.tag === 'ol' ? 'number' : 'bullet'),
    })}\n`;
  }

  if (node.type === 'listitem') {
    return renderMarkdownListItem(node, context);
  }

  if (node.type === 'link' || node.type === 'autolink') {
    const label = renderMarkdownChildren(node.children, context).trim() || node.url || 'link';
    return `[${label}](${node.url || '#'})`;
  }

  return renderMarkdownChildren(node.children, context);
}

function getDocumentBody(file) {
  if (file.editorState) {
    try {
      const parsed = JSON.parse(file.editorState);
      const children = parsed?.root?.children;

      if (Array.isArray(children)) {
        return renderChildren(children);
      }
    } catch {
      // Fall back to plain text below.
    }
  }

  return String(file.content || '')
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map((line) => `<p>${escapeHtml(line) || '<br />'}</p>`)
    .join('');
}

export function buildFileHtml(file) {
  const appearance = { ...DEFAULT_APPEARANCE, ...(file.appearance || {}) };
  const backgroundColor = safeCssValue(appearance.backgroundColor, DEFAULT_APPEARANCE.backgroundColor);
  const textColor = safeCssValue(appearance.textColor, DEFAULT_APPEARANCE.textColor);
  const fontFamily = safeCssValue(appearance.fontFamily, DEFAULT_APPEARANCE.fontFamily);
  const fontSize = Number.isFinite(Number(appearance.fontSize))
    ? Number(appearance.fontSize)
    : DEFAULT_APPEARANCE.fontSize;
  const lineHeight = Number.isFinite(Number(appearance.lineHeight))
    ? Number(appearance.lineHeight)
    : DEFAULT_APPEARANCE.lineHeight;
  const title = escapeHtml(file.name || 'Untitled');
  const body = getDocumentBody(file);

  return `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${title}</title>
  <style>
    :root {
      color-scheme: light dark;
    }
    body {
      margin: 0;
      background: ${backgroundColor};
      color: ${textColor};
      font-family: ${fontFamily};
      font-size: ${fontSize}px;
      line-height: ${lineHeight};
    }
    main {
      max-width: 840px;
      margin: 0 auto;
      padding: 48px 24px;
    }
    p {
      margin: 0 0 0.8em;
    }
    h1, h2, h3, h4, h5, h6 {
      margin: 0 0 0.72em;
      line-height: 1.2;
    }
    blockquote {
      margin: 0 0 0.9em;
      padding-left: 16px;
      border-left: 3px solid currentColor;
      opacity: 0.82;
    }
    ul, ol {
      margin: 0 0 0.9em;
      padding-left: 24px;
    }
    li {
      margin: 0.24em 0;
    }
    a {
      color: #185abd;
    }
    code {
      font-family: "SFMono-Regular", Consolas, "Liberation Mono", monospace;
      font-size: 0.92em;
    }
  </style>
</head>
<body>
  <main>
${body}
  </main>
</body>
</html>`;
}

export function downloadHtmlFile(file) {
  const blob = new Blob([buildFileHtml(file)], { type: 'text/html;charset=utf-8' });
  downloadBlob(blob, `${getBaseFileName(file)}.html`);
}

export function buildFileMarkdown(file) {
  if (file.editorState) {
    try {
      const parsed = JSON.parse(file.editorState);
      const children = parsed?.root?.children;

      if (Array.isArray(children)) {
        return renderMarkdownChildren(children).trimEnd();
      }
    } catch {
      // Fall back to plain text below.
    }
  }

  return file.content || '';
}

export function downloadMarkdownFile(file) {
  const blob = new Blob([buildFileMarkdown(file)], { type: 'text/markdown;charset=utf-8' });
  downloadBlob(blob, `${getBaseFileName(file)}.md`);
}

export function buildTextLabFileJson(file) {
  return JSON.stringify(
    {
      version: 1,
      type: 'textlab-file',
      file,
    },
    null,
    2
  );
}

export function downloadTextLabFileJson(file) {
  const blob = new Blob([buildTextLabFileJson(file)], {
    type: 'application/json;charset=utf-8',
  });
  downloadBlob(blob, `${getBaseFileName(file)}.json`);
}

export function downloadFileByFormat(file, format) {
  if (format === 'html') {
    downloadHtmlFile(file);
    return;
  }

  if (format === 'markdown') {
    downloadMarkdownFile(file);
    return;
  }

  if (format === 'json') {
    downloadTextLabFileJson(file);
    return;
  }

  downloadTextFile(file);
}

function getFileContentByFormat(file, format) {
  if (format === 'html') {
    return {
      content: buildFileHtml(file),
      extension: 'html',
    };
  }

  if (format === 'markdown') {
    return {
      content: buildFileMarkdown(file),
      extension: 'md',
    };
  }

  if (format === 'json') {
    return {
      content: buildTextLabFileJson(file),
      extension: 'json',
    };
  }

  return {
    content: file.content || '',
    extension: 'txt',
  };
}

function buildRelativeFolderPath(folderId, rootFolder, folderMap) {
  const parts = [];
  let cursor = folderMap.get(folderId);

  while (cursor && cursor.id !== rootFolder.id) {
    parts.unshift(sanitizeFileName(cursor.name));
    cursor = cursor.parentId ? folderMap.get(cursor.parentId) : null;
  }

  parts.unshift(sanitizeFileName(rootFolder.name));
  return parts.join('/');
}

export async function downloadFolderZip(folder, folders, files, format = 'txt') {
  const zip = new JSZip();
  const folderMap = new Map(folders.map((item) => [item.id, item]));
  const includedFolderIds = new Set([
    folder.id,
    ...getDescendantFolderIds(folders, folder.id),
  ]);

  folders
    .filter((item) => includedFolderIds.has(item.id))
    .forEach((item) => {
      zip.folder(buildRelativeFolderPath(item.id, folder, folderMap));
    });

  files
    .filter((file) => includedFolderIds.has(file.parentFolderId))
    .forEach((file) => {
      const { content, extension } = getFileContentByFormat(file, format);
      const folderPath = buildRelativeFolderPath(file.parentFolderId, folder, folderMap);
      zip.file(`${folderPath}/${getBaseFileName(file)}.${extension}`, content);
    });

  const blob = await zip.generateAsync({ type: 'blob' });
  const label = format === 'markdown' ? 'md' : format;
  downloadBlob(blob, `${sanitizeFileName(folder.name)}-${label}.zip`);
}
